import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import Link from "next/link";
import { useMemo, useState, useEffect } from "react";
import DynamicParticles, { ISourceOptions } from "react-tsparticles";
import { useMeasure } from "react-use";
import "simplebar/dist/simplebar.min.css";
import Avatar from "../../common/components/Avatar";
import Banner from "../../common/components/Banner";
import Header from "../../common/components/Header";
import SmartImage from "../../common/components/SmartImage";
import { Art, Collection, LOCALE_OPTIONS } from "../../common/lib/api/base";
import { useBreakpoint } from "../../common/lib/hooks/useWindow";
import { makeAdmin } from "../../common/lib/makeAdmin";
import {
  cs,
  getArtLowQualUrl,
  getArtPreviewUrl,
  toDocs,
  togglePush,
  toNatural,
} from "../../common/lib/utils";
import particleConfig from "../../common/particlesjs-config.json";
import {
  HEADER_HEIGHT_MEDIUM,
  HEADER_HEIGHT_MOBILE,
} from "../../common/styles/mixins";
import { Container, Content } from "../../styles/ArtistsList";
import {
  Artists,
  Artworks,
  Filters,
  Grid,
  Main,
  Tags,
} from "../../styles/ArtsList";

import { useDebounce } from "react-use";

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const firestore = (await makeAdmin()).firestore();
  const locale = ctx.locale as LOCALE_OPTIONS;
  const artApi = firestore.collection("arts");
  const artistApi = firestore.collection("collections");
  const [arts, artists] = await Promise.all([
    toDocs<Art>(artApi.get()),
    toDocs<Collection>(artistApi.orderBy("name").get()),
  ]);
  return {
    props: {
      arts: arts
        .map((art) => ({ ...art, ...art.overrides?.[locale] }))
        .sort((a, b) => {
          if (a.collection.name < b.collection.name) return -1;
          if (a.collection.name > b.collection.name) return 1;
          return 0;
        }),
      artists: artists.map((artist) => ({
        ...artist,
        ...artist.overrides?.[locale],
      })),
      tags: arts.reduce((prev, curr) => {
        return Array.from(
          new Set([
            ...prev,
            ...(curr.overrides?.[locale]?.tags?.map(({ value }) => value) ||
              []),
          ])
        );
      }, [] as string[]),
    },
  };
};

// const useFilterArt = (
//   tags: string[],
//   artists: Collection[],
//   search: string
// ) => {
//   const [artMap, setArtMap] = useState({} as MapOf<Art>);
//   const firestore = makeFirebase().firestore();
//   const artApi = new Arts(firestore);
//   useEffect(() => {
//     (async function () {
//       const [tagResult, artistResult, searchResult] = await Promise.all([
//         artApi.getAll((ref) => {
//           return ref.where("overrides.tags", "array-contains-any", tags);
//         }),
//         artApi.getAll((ref) =>
//           ref.where(
//             "collection.slug",
//             "in",
//             artists.map(({ slug }) => slug)
//           )
//         ),
//         artApi.getAll((ref) => ref.orderBy("name").startAt(search)),
//       ]);
//       setArtMap(
//         [...tagResult, ...artistResult, ...searchResult].reduce(
//           (prev, curr) => {
//             prev[curr.id] = curr;
//             return prev;
//           },
//           {} as MapOf<Art>
//         )
//       );
//     })();
//   }, [tags, artists, search]);
//   return {
//     data: Object.values(artMap),
//   };
// };

const ArtsIndex: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ arts, tags, artists }) => {
  const isMedium = useBreakpoint("MEDIUM");
  const router = useRouter();
  const { locale, query } = router;
  const [ref, dim] = useMeasure<any>();
  const t = useTranslations("ArtsList");
  // Tag logic
  const [selectedTags, setSelectedTags] = useState(
    (query.tags ? JSON.parse(query.tags as string) : []) as string[]
  );
  // const groupedTags = useMemo(
  //   () =>
  //     tags
  //       .reduce(
  //         (prev, curr) => {
  //           const position = Number(!selectedTags.includes(curr)); // 0 or 1
  //           prev[position].push(curr);
  //           return prev;
  //         },
  //         [[], []] as string[][]
  //       )
  //       .map((arr) => arr.sort()),
  //   [selectedTags]
  // );
  const handleTagClick = (tag: string) => {
    setSelectedTags((prev) => togglePush(prev, tag));
  };
  // Collection logic
  const [selectedArtists, setSelectedArtists] = useState(
    (query.artists ? JSON.parse(query.artists as string) : []) as string[]
  );

  const groupedArtists = useMemo(
    () =>
      artists
        .reduce(
          (prev, curr) => {
            const position = Number(!selectedArtists.includes(curr.slug)); // 0 or 1
            prev[position].push(curr);
            return prev;
          },
          [[], []] as Collection[][]
        )
        .map((arr) => arr.sort(({ name: a }, { name: b }) => Number(a > b))),
    [selectedArtists]
  );
  const handleArtistClick = (artistId: string) => {
    setSelectedArtists((prev) => togglePush(prev, artistId));
  };
  const querySearch = (query.search as string) || "";
  const [baseSearch, setBaseSearch] = useState(querySearch);
  const [search, setSearch] = useState(querySearch);
  const [,] = useDebounce(
    () => {
      setSearch(baseSearch);
    },
    150,
    [baseSearch]
  );
  const hasSearch =
    search.length > 2 || selectedTags.length > 0 || selectedArtists.length > 0;
  const filteredArts = useMemo(() => {
    return arts.filter((art) => {
      if (
        selectedTags.length > 0 &&
        !art.overrides?.[locale as LOCALE_OPTIONS]?.tags?.some((tag) =>
          selectedTags.includes(tag.value)
        )
      )
        return false;
      if (
        selectedArtists.length > 0 &&
        !selectedArtists.includes(art.collection.slug)
      )
        return false;
      if (search.length > 2 && !art.name.match(new RegExp(search, "gi")))
        return false;

      return true;
    });
  }, [selectedTags, selectedArtists, search]);

  useEffect(() => {
    const nextRoute = {
      pathname: router.pathname,
      query: router.query,
    };

    if (selectedTags.length > 0)
      nextRoute.query.tags = JSON.stringify(selectedTags);
    else delete nextRoute.query.tags;

    if (selectedArtists.length > 0)
      nextRoute.query.artists = JSON.stringify(selectedArtists);
    else delete nextRoute.query.artists;

    if (search.length >= 3) nextRoute.query.search = search;
    else delete nextRoute.query.search;

    router.push(nextRoute, undefined, { scroll: false });
  }, [selectedTags, selectedArtists, search]);

  // useFilterArt
  const renderArtist = (
    artist: Collection,
    _: number,
    __: Collection[],
    isSelected?: boolean
  ) => {
    return (
      <div
        className="artist-info"
        style={{ alignItems: "center" }}
        onClick={() => {
          handleArtistClick(artist.slug);
        }}
        key={artist.slug}
      >
        <Avatar
          src={
            isSelected
              ? "/checkmark.png"
              : artist.image_url.replace(/(\=s\d*)$/, () => "=s42")
          }
          size={38}
          glow="off"
          style={isSelected ? { height: 20, width: 20, margin: 9 } : {}}
        />
        <h4>{artist.name}</h4>
      </div>
    );
  };
  return (
    <>
      <Header />
      <Banner header ref={ref}>
        <DynamicParticles
          height={`${
            dim.height +
            (isMedium ? HEADER_HEIGHT_MEDIUM : HEADER_HEIGHT_MOBILE)
          }px`}
          id="tsparticles"
          options={particleConfig as unknown as ISourceOptions}
        />
        <Content>
          <Container>
            <h1>{t("artworks")}</h1>
            <p>{t("subtitle")}</p>
          </Container>
        </Content>
        <div className="shadow"></div>
      </Banner>
      <Main>
        <Filters>
          <h3>{t("filters")}</h3>
          <div>
            <input
              type="text"
              name="search"
              id="search"
              placeholder={t("placeholder") as string}
              value={baseSearch}
              onChange={({ target: { value } }) => setBaseSearch(value)}
            />
          </div>
          <h3>Tags</h3>
          <Tags>
            {tags.map((tag) => (
              <div
                onClick={() => handleTagClick(tag)}
                className={selectedTags.includes(tag) ? "selected" : ""}
                key={tag}
              >
                {toNatural(tag)}
              </div>
            ))}
          </Tags>
          <h3>{t("artists")}</h3>
          <Artists>
            {groupedArtists[0].map((...args) => renderArtist(...args, true))}
            {groupedArtists[1].map(renderArtist)}
          </Artists>
        </Filters>
        <div style={{ flex: 1 }}>
          {hasSearch && filteredArts.length === 0 && <h3>No matches</h3>}
          <Artworks>{t("artworks")}</Artworks>
          <Grid>
            {arts.map((art, i) => {
              // if(finalDescription.length >
              const shouldHide =
                hasSearch &&
                filteredArts.findIndex((incArt) => {
                  return incArt.id === art.id;
                }) === -1;
              return (
                <div
                  key={art.id}
                  className={"grid-item"}
                  style={shouldHide ? { display: "none" } : {}}
                >
                  <Link href={`/arts/${art.slug}`}>
                    <a>
                      <SmartImage
                        glow="off"
                        src={getArtPreviewUrl(art, !isMedium)}
                        placeholder={getArtLowQualUrl(art)}
                      />
                    </a>
                  </Link>
                  <div className="item-content">
                    <div>
                      <h2 className="art-title">{art.name}</h2>
                    </div>
                    <Link href={`/artists/${art.collection.slug}`}>
                      <a>
                        <div className="artist-info">
                          <Avatar
                            src={art.collection.image_url.replace(
                              /(\=s\d*)$/,
                              () => "=s42"
                            )}
                            size={32}
                          />
                          <div className="created-by">
                            <span>Creator</span>
                            <h3>{art.collection.name}</h3>
                          </div>
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              );
            })}
          </Grid>
        </div>
      </Main>
    </>
  );
};

export default ArtsIndex;
